// « Administration fiscale » (P3 F4c-2; PMS-P3-FISCAL-SCHEMA-BRIEF §11.14 point 7, §11.19; référentiel E19–E21).
//
// The screen the administration's agents use during a control, and the operator before one: the fiscal version and
// the perimeter's fingerprint, the caisses and their chains, the key register, the clôtures with their totals and
// their proofs, the archives — downloadable as one folder carrying its own verifier — and « Vérifier l'intégrité ».
//
// Read-only except three things, each traced in the JET by the database: a verification (its verdict), an archive
// download (an `export` event), an on-demand archive request. `pms.fiscal.audit` gates every call in the database;
// hiding the tab is only the courtesy of not offering what would be refused (ARCHITECTURE §1).

import { useCallback, useEffect, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import {
  FISCAL_MANUAL_FR,
  FISCAL_PERIMETER,
  inspectorState,
  type FiscalAuditArchive,
  type FiscalAuditCaisse,
  type FiscalAuditOverview,
  type FiscalInspector,
  type InspectorAccessOpened,
  type FiscalVerificationRun,
} from '@lemontaigu/pms-core';
import { usePms, usePmsScope } from './PmsProvider.js';
import { fmtEuros, isoAddDays } from './logic.js';
import { ManualView } from './manualView.js';
import {
  ARCHIVE_KIND_FR,
  CLOSURE_KIND_FR,
  closureSealFr,
  closureSealed,
  keyPurposeFr,
  keyStateFr,
  otsFr,
  periodFr,
  shortHash,
  tenderFr,
} from './fiscalAuditView.js';

const ts = (s: string | null) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)} ${s.slice(11, 16)}` : '—');

export function AdministrationFiscale() {
  const { fiscalAudit } = usePms();
  const { current } = usePmsScope();
  const today = todayIsoParis();
  const [from, setFrom] = useState(isoAddDays(today, -31));
  const [to, setTo] = useState(today);
  const [data, setData] = useState<FiscalAuditOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canManageInspectors = useCapability(CAPABILITIES.PMS_FISCAL_INSPECTOR_MANAGE);

  const load = useCallback(async () => {
    if (!current) return;
    const r = await fiscalAudit.overview(current, from, to);
    if (r.ok) {
      setData(r.value);
      setError(null);
    } else setError(r.error.message);
  }, [fiscalAudit, current, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="pms-warn" role="alert">{error}</p>;
  if (!data) return <p className="pms-hint">Chargement…</p>;
  const caisse = (id: number) => data.caisses.find((c) => c.id === id);
  const canManage = canManageInspectors;
  const versionMatches = data.version === FISCAL_PERIMETER.version;

  return (
    <div className="pms-fiscal-admin" data-testid="administration-fiscale">
      <div className="pms-head">
        <h2>Administration fiscale</h2>
        <span className="pms-hint">Accès en lecture — articles 286-I-3° bis du CGI et BOI-TVA-DECLA-30-10-30</span>
      </div>

      <section aria-labelledby="fa-version">
        <h3 id="fa-version">Logiciel</h3>
        <dl className="pms-fiscal-dl">
          <dt>Version fiscale</dt>
          <dd data-testid="fa-version">{data.version}</dd>
          <dt>Empreinte du périmètre fiscal</dt>
          <dd>
            <code>{FISCAL_PERIMETER.fingerprint}</code>{' '}
            <span className="pms-sub">(SHA-256 de {FISCAL_PERIMETER.fileCount} fichiers sources, version {FISCAL_PERIMETER.version})</span>
          </dd>
        </dl>
        <details className="pms-fiscal-manual-box" data-testid="fa-manual">
          <summary>Manuel à l’usage de l’administration fiscale (version {FISCAL_PERIMETER.version})</summary>
          <ManualView markdown={FISCAL_MANUAL_FR} />
        </details>
        {!versionMatches && (
          <p className="pms-warn" role="alert">
            Cette application a été construite pour la version {FISCAL_PERIMETER.version} ; la base enregistre la
            version {data.version}. Rechargez l’application.
          </p>
        )}
      </section>

      <section aria-labelledby="fa-caisses">
        <h3 id="fa-caisses">Caisses et chaînage</h3>
        <div className="pms-table-wrap">
          <table className="pms-table">
            <thead>
              <tr>
                <th>Caisse</th>
                <th>Ouverte le</th>
                <th>Clôtures depuis</th>
                <th className="pms-num">Dernier rang</th>
                <th>Tête de chaîne</th>
                <th>Intégrité</th>
              </tr>
            </thead>
            <tbody>
              {data.caisses.map((c) => (
                <CaisseRow key={c.id} caisse={c} onDone={() => void load()} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="fa-keys">
        <h3 id="fa-keys">Registre des clés</h3>
        <div className="pms-table-wrap">
          <table className="pms-table">
            <thead>
              <tr>
                <th>Clé</th>
                <th>Usage</th>
                <th>Clé publique</th>
                <th>Depuis</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {data.keys.map((k) => (
                <tr key={k.keyId}>
                  <td><code>{k.keyId}</code></td>
                  <td>{keyPurposeFr(k)}</td>
                  <td>{k.publicKey ? <code className="pms-break">{k.publicKey}</code> : <span className="pms-sub">secrète (coffre)</span>}</td>
                  <td>{ts(k.validFrom)}</td>
                  <td>{keyStateFr(k, new Date().toISOString())}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="fa-closures">
        <h3 id="fa-closures">Clôtures</h3>
        <div className="pms-toolbar">
          <label>
            Journalières du{' '}
            <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          </label>
          <label>
            au <input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </label>
          <span className="pms-hint">Les clôtures mensuelles et d’exercice sont toutes affichées.</span>
        </div>
        <div className="pms-table-wrap">
          <table className="pms-table" data-testid="fa-closures">
            <thead>
              <tr>
                <th>Caisse</th>
                <th>Clôture</th>
                <th>Période</th>
                <th className="pms-num">Pièces</th>
                <th className="pms-num">Règlements</th>
                <th className="pms-num">Total de la période</th>
                <th className="pms-num">Cumul de l’exercice</th>
                <th className="pms-num">Total perpétuel</th>
                <th>TVA · encaissements</th>
                <th>Preuve</th>
              </tr>
            </thead>
            <tbody>
              {data.closures.length === 0 && (
                <tr><td colSpan={10} className="pms-hint">Aucune clôture sur cette période.</td></tr>
              )}
              {data.closures.map((x) => (
                <tr key={x.id} className={x.kind === 'daily' ? undefined : 'pms-fiscal-period'}>
                  <td>{caisse(x.caisseId)?.code}{x.isTraining && <span className="pms-sub"> (école)</span>}</td>
                  <td>{CLOSURE_KIND_FR[x.kind]}</td>
                  <td>{periodFr(x.kind, x.periodStart, x.periodEnd)}</td>
                  <td className="pms-num">{x.documentCount}</td>
                  <td className="pms-num">{x.paymentCount}</td>
                  <td className="pms-num">{fmtEuros(x.periodTotalCents)}</td>
                  <td className="pms-num">{fmtEuros(x.grandTotalCents)}</td>
                  <td className="pms-num">{fmtEuros(x.perpetualTotalCents)}</td>
                  <td className="pms-sub">
                    {x.vatBreakdown.map((v) => (
                      <div key={`v${v.rate}`}>TVA {v.rate ?? '—'} % : {fmtEuros(Number(v.vat_cents))} sur {fmtEuros(Number(v.ht_cents))} HT</div>
                    ))}
                    {x.tenderTakings.map((t) => (
                      <div key={`t${t.tender}${t.movement}`}>{tenderFr(t.tender, t.movement)} : {t.count} · {fmtEuros(Number(t.amount_cents))}</div>
                    ))}
                  </td>
                  <td className={closureSealed(x) ? 'pms-sub' : 'pms-warn-text'}>{closureSealFr(x)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="fa-archives">
        <h3 id="fa-archives">Archives fiscales</h3>
        <p className="pms-hint">
          Chaque archive est un dossier autonome : les données au format JSON, leur manifeste signé et horodaté, une
          notice en français et le programme de contrôle. Le téléchargement est inscrit au journal des événements.
        </p>
        <div className="pms-table-wrap">
          <table className="pms-table" data-testid="fa-archives">
            <thead>
              <tr>
                <th>Caisse</th>
                <th>Archive</th>
                <th>Période</th>
                <th>Produite le</th>
                <th>Empreinte du manifeste</th>
                <th>Preuve</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.archives.length === 0 && (
                <tr><td colSpan={7} className="pms-hint">Aucune archive pour l’instant — la première suit la première clôture mensuelle.</td></tr>
              )}
              {data.archives.map((a) => (
                <ArchiveRow key={a.id} archive={a} caisseCode={caisse(a.caisseId)?.code ?? String(a.caisseId)} />
              ))}
            </tbody>
          </table>
        </div>
        <RequestArchive caisses={data.caisses} onRequested={() => void load()} />
        {data.requests.some((q) => q.archiveId === null) && (
          <ul className="pms-fiscal-requests">
            {data.requests.filter((q) => q.archiveId === null).map((q) => (
              <li key={q.id}>
                Demande n° {q.id} — {caisse(q.caisseId)?.code}, {periodFr('range', q.periodStart, q.periodEnd)}, le{' '}
                {ts(q.requestedAt)}{q.requestedBy ? ` par ${q.requestedBy}` : ''} : en cours de production
                {q.lastError && <span className="pms-warn-text"> — dernier essai : {q.lastError}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {canManage && current && <Inspectors propertyCode={current} />}

      <section aria-labelledby="fa-verifs">
        <h3 id="fa-verifs">Dernières vérifications</h3>
        {data.verifications.length === 0 ? (
          <p className="pms-hint">Aucune vérification enregistrée.</p>
        ) : (
          <ul>
            {data.verifications.map((v, i) => (
              <li key={i}>
                {ts(v.occurredAt)} — {caisse(v.caisseId)?.code} — {v.method === 'sql' ? 'en base' : 'programme de contrôle'}
                {v.by ? `, par ${v.by}` : ''} : {v.ok === 'true' ? 'intègre' : `${v.findings ?? '?'} anomalie(s)`}
                {v.lastSequence ? ` (rangs 1 → ${v.lastSequence})` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CaisseRow({ caisse, onDone }: { caisse: FiscalAuditCaisse; onDone: () => void }) {
  const { fiscalAudit } = usePms();
  const [run, setRun] = useState<FiscalVerificationRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verify = async () => {
    setBusy(true);
    setError(null);
    let r = await fiscalAudit.startVerification(caisse.id);
    while (r.ok && !r.value.done) {
      setRun(r.value);
      r = await fiscalAudit.stepVerification(r.value.runId);
    }
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setRun(r.value);
    onDone();
  };

  return (
    <>
      <tr>
        <td>
          {caisse.code}
          {caisse.trainingOnly && <span className="pms-sub"> (école)</span>}
        </td>
        <td>{ts(caisse.openedAt)}</td>
        <td>{caisse.closuresFrom ? periodFr('daily', caisse.closuresFrom, caisse.closuresFrom) : '—'}</td>
        <td className="pms-num">{caisse.lastSequence}</td>
        <td><code title={caisse.chainHead}>{shortHash(caisse.chainHead)}</code></td>
        <td>
          <button type="button" className="pms-chip" disabled={busy} onClick={() => void verify()}>
            {busy ? `Vérification… ${run ? `${run.checkedThrough} / ${run.targetSequence}` : ''}` : 'Vérifier l’intégrité'}
          </button>
        </td>
      </tr>
      {(run?.done || error) && (
        <tr>
          <td colSpan={6} data-testid={`fa-verdict-${caisse.code}`}>
            {error && <span className="pms-warn-text" role="alert">{error}</span>}
            {run?.done && (
              <div className={run.ok ? 'pms-fiscal-ok' : 'pms-warn'}>
                {run.ok
                  ? `Chaîne intègre : ${run.links} maillon(s)${run.documentsV1 ? ` et ${run.documentsV1} pièce(s) v1` : ''}, ${run.closures} clôture(s) recalculée(s) — rangs 1 → ${run.targetSequence}.`
                  : `${run.findingCount} anomalie(s) sur les rangs 1 → ${run.targetSequence} :`}
                {!run.ok && <ul>{run.findings.map((f, i) => <li key={i}>{f}</li>)}</ul>}
                <div className="pms-sub">
                  Vérification inscrite au journal des événements (n° {run.eventId}).
                  {run.closuresNotYetSealed > 0 && ` ${run.closuresNotYetSealed} clôture(s) pas encore signée(s) ou horodatée(s).`}{' '}
                  Les signatures et horodatages se contrôlent avec le programme fourni dans chaque archive.
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function ArchiveRow({ archive, caisseCode }: { archive: FiscalAuditArchive; caisseCode: string }) {
  const { fiscalAudit } = usePms();
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setError(null);
    setProgress('Ouverture…');
    const r = await fiscalAudit.downloadArchive(archive, caisseCode, (done, total) => setProgress(`${done} / ${total} fichiers`));
    setProgress(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    const url = URL.createObjectURL(new Blob([r.value.bytes as Uint8Array<ArrayBuffer>], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = r.value.fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <tr>
      <td>{caisseCode}</td>
      <td>
        {ARCHIVE_KIND_FR[archive.kind]}
        {archive.requestedBy && <div className="pms-sub">demandée par {archive.requestedBy}</div>}
      </td>
      <td>{periodFr(archive.kind === 'on_demand' ? 'range' : archive.kind, archive.periodStart, archive.periodEnd)}</td>
      <td>{ts(archive.generatedAt)}</td>
      <td><code title={archive.manifestSha256}>{shortHash(archive.manifestSha256)}</code></td>
      <td className="pms-sub">{archive.sealed ? `Signée et horodatée · ${otsFr(archive.ots)}` : 'Scellement en cours'}</td>
      <td>
        <button type="button" className="pms-chip" disabled={!archive.sealed || progress !== null} onClick={() => void download()}>
          {progress ?? 'Télécharger (.zip)'}
        </button>
        {error && <div className="pms-warn-text" role="alert">{error}</div>}
      </td>
    </tr>
  );
}

function RequestArchive({ caisses, onRequested }: { caisses: FiscalAuditCaisse[]; onRequested: () => void }) {
  const { fiscalAudit } = usePms();
  const yesterday = isoAddDays(todayIsoParis(), -1);
  const [caisseId, setCaisseId] = useState<number | null>(caisses.find((c) => !c.trainingOnly)?.id ?? caisses[0]?.id ?? null);
  const [from, setFrom] = useState(isoAddDays(yesterday, -6));
  const [to, setTo] = useState(yesterday);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    if (caisseId === null) return;
    setBusy(true);
    const r = await fiscalAudit.requestArchive(caisseId, from, to, reason.trim() || null);
    setBusy(false);
    if (r.ok) {
      setMessage({ ok: true, text: `Demande n° ${r.value} enregistrée — l’archive sera produite dans l’heure.` });
      setReason('');
      onRequested();
    } else setMessage({ ok: false, text: r.error.message });
  };

  return (
    <form
      className="pms-toolbar pms-fiscal-request"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <strong>Demander une archive</strong>
      <select value={caisseId ?? ''} onChange={(e) => setCaisseId(Number(e.target.value))} aria-label="Caisse">
        {caisses.map((c) => (
          <option key={c.id} value={c.id}>{c.code}</option>
        ))}
      </select>
      <label>du <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
      <label>au <input type="date" value={to} min={from} max={yesterday} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
      <input type="text" placeholder="Motif (facultatif)" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
      <button type="submit" className="pms-chip" disabled={busy || caisseId === null}>Demander</button>
      {message && <span className={message.ok ? 'pms-hint' : 'pms-warn-text'} role="status">{message.text}</span>}
    </form>
  );
}

const INSPECTOR_STATE_FR = { open: 'Ouvert', expired: 'Expiré', revoked: 'Retiré' } as const;

/** The inspector register and its two actions (F4c-3; `pms.fiscal.inspector.manage`, the operator only). */
function Inspectors({ propertyCode }: { propertyCode: string }) {
  const { fiscalAudit } = usePms();
  const [list, setList] = useState<FiscalInspector[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<InspectorAccessOpened | null>(null);
  const [form, setForm] = useState({ email: '', fullName: '', authority: '', days: 30 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fiscalAudit.inspectors(propertyCode);
    if (r.ok) setList(r.value);
    else setError(r.error.message);
  }, [fiscalAudit, propertyCode]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async () => {
    setBusy(true);
    setError(null);
    const r = await fiscalAudit.openInspector({
      propertyCode,
      email: form.email,
      fullName: form.fullName,
      authority: form.authority.trim() || null,
      days: form.days,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setOpened(r.value);
    setForm({ email: '', fullName: '', authority: '', days: 30 });
    void load();
  };

  const revoke = async (i: FiscalInspector) => {
    if (!window.confirm(`Retirer l’accès de ${i.fullName} (${i.email}) maintenant ?`)) return;
    const r = await fiscalAudit.revokeInspector(i.id);
    if (!r.ok) setError(r.error.message);
    void load();
  };

  const now = new Date();
  return (
    <section aria-labelledby="fa-inspectors" data-testid="fa-inspectors">
      <h3 id="fa-inspectors">Accès des inspecteurs</h3>
      <p className="pms-hint">
        Un accès en lecture à cet écran et aux factures, pour une durée limitée, retiré automatiquement à son terme.
        Chaque ouverture et chaque retrait sont inscrits au journal des événements. Le mot de passe provisoire
        s’affiche une seule fois : remettez-le en main propre ; l’inspecteur associe ensuite une application
        d’authentification à sa première connexion.
      </p>
      {opened && (
        <div className="pms-fiscal-ok" role="status" data-testid="fa-inspector-opened">
          Accès ouvert pour <strong>{opened.email}</strong> jusqu’au {opened.expiresAt.slice(0, 10)}. Mot de passe
          provisoire : <code className="pms-fiscal-password">{opened.password}</code>
          <div className="pms-sub">Il ne sera plus affiché. Adresse de connexion : celle de ce PMS.</div>
          <button type="button" className="pms-link" onClick={() => setOpened(null)}>Masquer</button>
        </div>
      )}
      <form
        className="pms-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          void open();
        }}
      >
        <input type="email" required placeholder="E-mail de l’inspecteur" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input type="text" required placeholder="Nom et prénom" value={form.fullName}
          onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        <input type="text" placeholder="Service (ex. DGFiP — brigade)" value={form.authority} maxLength={200}
          onChange={(e) => setForm({ ...form, authority: e.target.value })} />
        <label>
          durée{' '}
          <input type="number" min={1} max={90} value={form.days} style={{ width: '4.5rem' }}
            onChange={(e) => setForm({ ...form, days: Number(e.target.value) })} />{' '}
          jours
        </label>
        <button type="submit" className="pms-chip" disabled={busy}>Ouvrir l’accès</button>
      </form>
      {error && <p className="pms-warn-text" role="alert">{error}</p>}
      {list && list.length > 0 && (
        <div className="pms-table-wrap">
          <table className="pms-table">
            <thead>
              <tr>
                <th>Inspecteur</th>
                <th>Service</th>
                <th>Ouvert le</th>
                <th>Jusqu’au</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((i) => {
                const state = inspectorState(i, now);
                return (
                  <tr key={i.id}>
                    <td>{i.fullName}<div className="pms-sub">{i.email}</div></td>
                    <td>{i.authority ?? '—'}</td>
                    <td>{ts(i.grantedAt)}</td>
                    <td>{ts(i.expiresAt)}</td>
                    <td>{INSPECTOR_STATE_FR[state]}</td>
                    <td>
                      {state === 'open' && (
                        <button type="button" className="pms-chip" onClick={() => void revoke(i)}>Retirer l’accès</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
